import React, { useLayoutEffect, useRef } from 'react';
import {Button} from 'react-bootstrap';
import {Popup, useMap} from 'react-leaflet';
import LabelTag from "./LabelTagComponent";
import {Link} from "react-router-dom";
import noReception from "../Images/no_reception.png"
import {BuildingSummary} from "../types/protocols";
import {colorForBuildingType, iconForBuildingType, iconForGroupTag} from "../types/BuildingTypeEnums";
import {placeholderForBuildingType} from "../Helpers/PlaceholderUtils";
import {FirebaseImage, ImageSize} from "../Helpers/ImageLoader";
import COLORS, {getCoverPhotoStoragePath} from "../Helpers/utils";
import {translate} from "../types/EnumTranslations";

const MAX_ZOOM = 18;

// Pans the map so the whole popup is visible. autoPan is off and MapComponent
// only recenters when the marker is far off-center, which is fine on a tall
// desktop map but clips popups on the short phone map band. Rendered inside
// the Popup so it mounts when the popup opens, after the content has its real
// size. If a pan animation is running, wait for it: positions come from the DOM.
const FitPopupInView: React.FC = () => {
    const map = useMap();
    const anchorRef = useRef<HTMLSpanElement>(null);

    useLayoutEffect(() => {
        const fit = () => {
            const popupEl = anchorRef.current?.closest<HTMLElement>('.leaflet-popup');
            if (!popupEl) return;
            const mapRect = map.getContainer().getBoundingClientRect();
            const rect = popupEl.getBoundingClientRect();
            const padding = 12;
            let panX = 0;
            let panY = 0;
            if (rect.top < mapRect.top + padding) panY = rect.top - (mapRect.top + padding);
            if (rect.left < mapRect.left + padding) panX = rect.left - (mapRect.left + padding);
            else if (rect.right > mapRect.right - padding) panX = rect.right - (mapRect.right - padding);
            if (panX || panY) map.panBy([panX, panY], { animate: false });
        };

        const animating =
            map.getPanes().mapPane.classList.contains('leaflet-pan-anim') ||
            map.getContainer().classList.contains('leaflet-zoom-anim');
        if (animating) {
            map.once('moveend', fit);
            return () => { map.off('moveend', fit); };
        }
        fit();
    }, [map]);

    return <span ref={anchorRef} hidden />;
};

interface MapPopupProps {
    building: BuildingSummary;
    onClose?: () => void;
}

const MapPopup: React.FC<MapPopupProps> = ({ building, onClose }) => {
    const map = useMap();

    const handleZoomClick = () => {
        if (!building.coordinates) return;
        map.flyTo([building.coordinates.latitude, building.coordinates.longitude], MAX_ZOOM, { duration: 1 });
    };

    return (
        <Popup
            autoPan={false}
            offset={[1, -20]}
            eventHandlers={{
                popupclose: () => {
                    onClose?.();
                },
                remove: () => {
                    onClose?.();
                }
            }}
        >
            <FitPopupInView />
            <div
                style={{
                    position: 'relative',
                    left: '-21px',
                    top: '-14px',
                    width: '102%',
                    height: '100px',
                    backgroundColor: COLORS.lightLightGrey,
                }}
            >
                <div style={{position: "absolute", left: '8px', top: '8px', fontSize: '12px'}}>
                    <LabelTag text={translate(building.buildingType)} backgroundColor='#F2F2F2' textColor='#000' imgSrc={iconForBuildingType(building.buildingType)} />
                </div>

                {building.buildingInfo?.hasPoorReception && (
                    <div style={{
                        position: 'absolute',
                        right: '-20px',
                        bottom: '8px',
                        backgroundColor: '#F2F2F2',
                        borderRadius: '3px',
                        padding: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <img src={noReception} style={{width: '20px', height: '20px'}}/>
                    </div>
                )}

                <FirebaseImage
                    storagePath={getCoverPhotoStoragePath(building)} size={ImageSize.THUMBNAIL_MEDIUM}
                    style={{
                        width: '115%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                    }}
                    placeholder={placeholderForBuildingType(building.buildingType)}
                />

                <Button
                    className="zoom-button-style"
                    style={{
                        position: 'absolute',
                        left: '8px',
                        bottom: '8px',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    variant="light"
                    onClick={handleZoomClick}
                    title="Zooma in på byggnaden"
                    aria-label="Zooma in på byggnaden"
                >
                    <i className="pi pi-search-plus" style={{ fontSize: '12px' }} />
                </Button>
            </div>

            <div style={{ display: 'flex', alignItems: 'stretch', maxWidth: '200px', marginTop: '5px' }}>
                <div style={{
                    width: '100%'
                }}>
                    <strong style={{ fontSize: '16px', fontFamily: 'Source Sans Pro', }}>
                        {building.name}
                    </strong>
                    <span style={{ marginTop: '8px', fontSize: '12px', fontFamily: 'Source Sans Pro', display: 'block' }}>
                        {building.locationCategories?.category2?.name || ''}
                        {building.locationCategories?.category2?.name && building.locationCategories?.category1?.name && <br />}
                        {building.locationCategories?.category1?.name || ''}
                    </span>
                    
                    <div style={{ marginTop: '15px' }}>
                    {
                        building.groupTags &&
                        <>{
                            Object.values(building.groupTags).map((tag, index)=> {
                                return <LabelTag key={`${tag.tag}-${index}`} text={tag.tag} backgroundColor='#F2F2F2' textColor='#000' marginBottom={3} imgSrc={iconForGroupTag(tag.tag)} />
                            })
                        }
                        </>
                    }
                    </div>
                    
                    <div style={{ marginTop: '45px' }}>

                    </div>
                </div>
            </div>

            <Link
                to={`/building/${building.key}`}
                className="btn btn-secondary small-button-style"
                style={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bottom: '10px',
                    borderRadius: '11px',
                    fontSize: '10px',
                    letterSpacing: '0.2px',
                    fontFamily: 'Poppins',
                    fontWeight: 600
                }}
            >
                ÖPPNA
            </Link>
        </Popup>
);
};

export default MapPopup;